import React, { useState } from 'react';
import AdminList, { TemplateNameCellRenderer } from '../../ui/admin/AdminList';
import { useQuery } from '@apollo/react-hooks';
import { Template, IdAndName } from '../../../shared/types';
import { Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import AdminListToolbar from '../../ui/admin/AdminListToolbar';
import {
    filterArrayOfObjectsBy,
    sortArray,
} from '../../../utils/sorting.utils';
import gql from 'graphql-tag';
import {
    premiumChannelsEnabled,
    topicsOrCategories,
} from '../../../utils/utils';
import { useHistory } from 'react-router-dom';
import { baseUrls } from '../../../utils/baseUrls.utils';

const name = 'Templates';

interface TemplatesTreeRowItem {
    _id: string;
    title: string;
    categoriesHierarchy: string[];
    categories?: IdAndName[];
    tags?: IdAndName[];
    publishedBy: string | boolean;
    channel?: IdAndName;
}

const TEMPLATES = gql`
    query getTemplates {
        templates {
            _id
            title
            categories {
                _id
                name
            }
            tags {
                _id
                name
            }
            publishedBy {
                _id
                name
            }
            channel {
                _id
                name
            }
        }
    }
`;

const transformTemplateToTree = (
    templates: Template[]
): TemplatesTreeRowItem[] => {
    const treeRowData: TemplatesTreeRowItem[] = [];
    templates.forEach((template: Template) => {
        if (!treeRowData.some((x) => x._id === template.publishedBy._id)) {
            treeRowData.push({
                _id: template.publishedBy._id,
                title: template.publishedBy.name,
                categoriesHierarchy: [template.publishedBy.name],
                publishedBy: false,
            });
        }
        treeRowData.push({
            _id: template._id,
            title: template.title,
            categoriesHierarchy: [template.publishedBy.name, template.title],
            categories: template.categories,
            tags: template.tags,
            publishedBy: template.publishedBy._id,
            channel: template.channel,
        });
    });
    return treeRowData.sort((a, b) => a.title.localeCompare(b.title));
};

const autoGroupColumnDef: ColDef = {
    menuTabs: [],
    sortable: false,
    headerName: 'Template name',
    minWidth: 504,
    cellRendererParams: {
        suppressCount: true,
        innerRenderer: (props: any) => (
            <TemplateNameCellRenderer wrap={false} addFromTemplate {...props} />
        ),
    },
};

const CategoryCellRenderer = ({ value }: { value?: IdAndName[] }) => (
    <Typography
        component="span"
        variant="body2"
        sx={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}
    >
        {value?.map((tag) => tag.name).join(', ')}
    </Typography>
);

const ArticleAddFromTemplate = (): JSX.Element => {
    const history = useHistory();
    const columnDefs: ColDef[] = [
        {
            colId: 'category-articles-col',
            headerName: topicsOrCategories(true, true),
            field: 'categories',
            sortable: false,
            width: 240,
            autoHeight: true,
            cellRenderer: CategoryCellRenderer,
            menuTabs: [],
        },
        {
            headerName: 'Tags',
            field: 'tags',
            width: 240,
            sortable: false,
            valueFormatter: (props: any) =>
                props.data.tags?.map((tag: IdAndName) => tag.name).join(', '),
            menuTabs: [],
        },
        ...(premiumChannelsEnabled(false)
            ? [
                  {
                      headerName: 'Channel',
                      field: 'channel',
                      width: 240,
                      sortable: false,
                      valueFormatter: (props: any) => props.data.channel?.name,
                      menuTabs: [],
                  },
              ]
            : []),
    ];

    const [searchString, setSearchString] = useState('');
    const queryTemplates = useQuery(TEMPLATES, { fetchPolicy: 'network-only' });
    const templates = (queryTemplates?.data?.templates as Template[]) ?? [];

    if (queryTemplates.loading) return <></>;

    const treeRowData = transformTemplateToTree(
        sortArray(
            filterArrayOfObjectsBy(templates, searchString, ['title']),
            'title'
        )
    );
    const templatesNumberWithoutDesks = treeRowData.filter(
        (item) => item.publishedBy
    ).length;
    const openTemplate = ({ data }: { data?: TemplatesTreeRowItem }) => {
        if (!data?.publishedBy) return;

        history.push(
            `${baseUrls.publisher.articles.addFromTemplate}/${window.encodeURIComponent(data._id)}`
        );
    };

    return (
        <AdminList
            title="Choose a template to create a new article"
            name={name}
            items={treeRowData}
            itemsNumber={templatesNumberWithoutDesks}
            loading={queryTemplates.loading}
            error={queryTemplates.error}
            columns={columnDefs}
            autoGroupColumn={autoGroupColumnDef}
            gridOptions={{
                onRowClicked: openTemplate,
            }}
            showAdd={false}
            treeData={true}
            entityForCaptionText={
                templatesNumberWithoutDesks > 1 ? 'Templates' : 'Template'
            }
            toolbar={
                <AdminListToolbar
                    searchString={searchString}
                    toolbarPlaceholder="Search templates..."
                    onQueryStringChange={setSearchString}
                />
            }
        />
    );
};

export default ArticleAddFromTemplate;
